import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { fetchTickets, fetchUnseenTickets, fetchUser, fetchStatuses, fetchUsers, fetchDepartments, getPriorities, getTags, getTypes } from "../../../redux/actions";
import MainContainer from "../../MainContainer";
import TicketsSearch from './TicketsSearch';
import TicketsAction from './TicketsAction';
import TicketsExport from "./TicketsExport";
import TicketsFilter from './TicketsFilter';
import TicketsTable from './TicketsTable';
import TicketModal from './TicketModal';
import TicketsLayout from "./TicketsLayout";
import TicketCategoryInfo from "./TicketCategoryInfo";
import LimitedNewButton from '../../LimitedNewButton';
import TicketsQuickFilters from './TicketsQuickFilters';
import { BsEnvelope } from 'react-icons/bs';
import { Button } from 'react-bootstrap';
import SummaryEmailModal from '../../../modals/Tickets/SummaryEmailModal';

const Tickets = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { category } = useParams();

	const user = useSelector(state => state?.userReducer?.user);
	const ticketsLoading = useSelector(state => state.ticketsReducer.loading);
	const tickets = useSelector(state => state.ticketsReducer.tickets);
	const total = useSelector(state => state.ticketsReducer.total);
	const unseenTickets = useSelector(state => state.unseenTicketsReducer.ids);

	const [showTicketModal, setShowTicketModal] = useState(false)
	const [showSummaryModal, setShowSummaryModal] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [params, setParams] = useState('');
	const [selection, setSelection] = useState([])
	const [columns, setColumns] = useState(() => {
		const defaultColumns = {
			title: { visible: true, label: 'Ticket' },
			status: { visible: true, label: 'Status' },
			department: { visible: true, label: 'Department' },
			type: { visible: false, label: 'Type' },
			priority: { visible: true, label: 'Priority' },
			tags: { visible: true, label: 'Tags' },
			user: { visible: true, label: 'Assignee' },
			transferred_to: { visible: true, label: 'Transferred To' },
			replies: { visible: false, label: 'Replies' },
			replied_at: { visible: true, label: 'Replied' },
			due_date: { visible: true, label: 'Due' },
			created_at: { visible: true, label: 'Created' },
			last_update_at: { visible: true, label: 'Last Update At' },
			closed_at: { visible: true, label: 'Closed Date' },
			created_by: { label: 'Created By', visible: true },
			closed_by: { label: 'Closed By', visible: true },
		};
		const savedColumns = localStorage.getItem('ticketTableColumns');
		return savedColumns ? { ...defaultColumns, ...JSON.parse(savedColumns) } : defaultColumns;
	});
	const [filters, setFilters] = useState({});
	const [sort, setSort] = useState({ by: 'created_at', direction: 'desc' });
	const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

	const data = {}
	const loading = {}
	const dataFetchers = {
		statuses: fetchStatuses,
		departments: fetchDepartments,
		priorities: getPriorities,
		types: getTypes,
		tags: getTags,
		users: fetchUsers,
		user: fetchUser,
	}
	for (const dataKey of Object.keys(dataFetchers)) {
		data[dataKey] = useSelector(state => state[dataKey + 'Reducer'][dataKey]);
		loading[dataKey] = useSelector(state => state[dataKey + 'Reducer']['loading']);
	}

	const cleanParams = (params) => {
		return Object.entries(params).reduce((param, [key, value]) => {
			if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
				param[key] = value;
			}
			return param;
		}, {});
	}

	const handleSearchDebounced = useCallback((text) => {
		const trimmed = (text ?? "").trim();

		setPagination((p) => ({ ...p, page: 1 }));
		setFilters((prev) => ({ ...prev, text: trimmed }));
	}, []);

	const handleFilterChange = (newFilters) => {
		setPagination((p) => ({ ...p, page: 1 }));
		setFilters(newFilters);
	};

	const handleSortChange = (column) => {
		setPagination((p) => ({ ...p, page: 1 }));
		setSort({
			by: column,
			direction: sort.by === column && sort.direction === 'asc' ? 'desc' : 'asc',
		});
	};

	const handlePageChange = (page, perPage) => {
		setPagination({ page, perPage });
	};

	const handleColumnVisibilityChange = (column) => {
		const updatedColumns = {
			...columns,
			[column]: {
				...columns[column],
				visible: !columns[column]['visible']
			},
		};
		setColumns(updatedColumns);
		localStorage.setItem('ticketTableColumns', JSON.stringify(updatedColumns));
	};

	const parseParam = (param, type) => {
		if (type === 'id') {
			return param ? param.split(',').map(id => parseInt(id)) : [];
		} else if (type === 'bool') {
			return param === undefined || param === null ? null : param === 'true';
		} else {
			return param ? param.split(',') : [];
		}
	};

	const getPathname = (newParams, activeCategory = category) => {
		if (activeCategory === undefined || activeCategory === null || activeCategory === '') {
			return '/tickets';
		}

		const isCategoryConflict = (
			(newParams.deleted !== undefined && (activeCategory === 'deleted' && newParams.deleted === false)) ||
			(newParams.assigned !== undefined && (activeCategory === 'assigned' && newParams.assigned !== true)) ||
			(newParams.assigned !== undefined && (activeCategory === 'unassigned' && newParams.assigned !== false)) ||
			(newParams.assignee !== undefined && (activeCategory === 'my' && (newParams.assignee.length !== 1 || newParams.assignee[0] !== user.id))) ||
			(newParams.status !== undefined && (['open', 'active', 'pending', 'closed', 'rejected', 'spam', 'overdue'].includes(activeCategory) && (newParams.status !== activeCategory)))
		);
		return isCategoryConflict ? '/tickets' : `/tickets/${activeCategory}`;
	};

	const filtersMatchCategory = (nextFilters, activeCategory) => {
		if (activeCategory === undefined || activeCategory === null || activeCategory === '') {
			return true;
		}
		if (activeCategory === 'transferred') {
			return true;
		}
		if (activeCategory === 'deleted') {
			return nextFilters.deleted === true;
		}
		if (activeCategory === 'assigned') {
			return nextFilters.assigned === true;
		}
		if (activeCategory === 'unassigned') {
			return nextFilters.assigned === false;
		}
		if (activeCategory === 'my') {
			return Array.isArray(nextFilters.assignee)
				&& nextFilters.assignee.length === 1
				&& nextFilters.assignee[0] === data.user?.id;
		}
		if (['open', 'active', 'pending', 'closed', 'rejected', 'spam', 'overdue'].includes(activeCategory)) {
			return nextFilters.status === activeCategory;
		}
		// Named routes that carry filters in the URL (critical, need_confirmation, …)
		return true;
	};

	// Keep list filters in sync with the sidebar category. Do not fetch here.
	useEffect(() => {
		if (!loading.user && !loading.statuses) {
			const queryParams = new URLSearchParams(window.location.search);
			setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
			setFilters({
				text: queryParams.get('text') ?? '',
				tag: parseParam(queryParams.get('tag'), 'id'),
				type: parseParam(queryParams.get('type'), 'id'),
				priority: parseParam(queryParams.get('priority'), 'id'),
				department: parseParam(queryParams.get('department'), 'id'),
				assignee: queryParams.get('assignee') ? parseParam(queryParams.get('assignee'), 'id') : (category === 'my' ? [data.user.id] : []),
				created_by: queryParams.get('created_by') ? parseParam(queryParams.get('created_by'), 'id') : (category === 'my' ? [data.user.id] : []),
				assigned: parseParam(queryParams.get('assigned'), 'bool') ?? (category === 'assigned' ? true : category === 'unassigned' ? false : null),
				status: queryParams.get('status') ?? (data.statuses.some(status => status.name.toLowerCase() === category) ? category : ''),
				replied: parseParam(queryParams.get('replied'), 'bool') ?? null,
				due_date: queryParams.get('due_date') ?? '',
				created_at: queryParams.get('created_at') ?? '',
				due_date_range: parseParam(queryParams.get('due_date_range'), 'date'),
				created_at_range: parseParam(queryParams.get('created_at_range'), 'date'),
				deleted: parseParam(queryParams.get('deleted'), 'bool') ?? (category === 'deleted' ? true : null),
			});
		}
	}, [data.user, data.statuses, category]);

	// Clear stale rows as soon as the submenu changes (avoid showing the previous list).
	useEffect(() => {
		dispatch({ type: 'GET_TICKETS', payload: { tickets: [], total: 0 }, loading: true });
		setSelection([]);
	}, [category, dispatch]);

	// Sync URL + fetch only after filters match the active category (prevents a wrong "all tickets" request).
	useEffect(() => {
		if (Object.keys(filters).length === 0) {
			return;
		}
		if (!filtersMatchCategory(filters, category)) {
			return;
		}

		const cleanedParams = cleanParams({
			...filters,
			sort_by: sort.by,
			sort_direction: sort.direction,
			page: pagination.page,
			per_page: pagination.perPage,
		});
		const newParams = new URLSearchParams(cleanedParams).toString();
		const pathname = getPathname(cleanedParams, category);
		const newHistory = {
			search: newParams,
			pathname,
		};

		const currentSearch = (window.location.search || '').replace(/^\?/, '');
		if (currentSearch !== newParams || window.location.pathname !== pathname) {
			history.replace(newHistory);
		}

		if (params !== newParams) {
			setParams(newParams);
		}

		dispatch(fetchTickets(newParams, { transferred: category === 'transferred' }));
		if (category !== 'transferred') {
			dispatch(fetchUnseenTickets());
		}
	}, [filters, sort, pagination, category]);

	useEffect(() => {
		setIsLoading(Object.values(loading).some(loading => loading) || ticketsLoading);
	}, [ticketsLoading, ...Object.values(loading)]);

	useEffect(() => {
		for (const dataKey of Object.keys(dataFetchers)) {
			if ((data[dataKey] === undefined || data[dataKey].length === 0) && !loading[dataKey]) {
				dispatch(dataFetchers[dataKey]());
			}
		}
	}, [filters]);

	const reduxRefresh = () => {
		dispatch(fetchTickets(params, { transferred: category === 'transferred' }));
		dispatch(fetchUser());
		if (category !== 'transferred') {
			dispatch(fetchUnseenTickets());
		}
	};

	const actionStarted = () => {
		setIsLoading(true);
	}

	const actionCompleted = () => {
		dispatch(fetchUser());
		setSelection([]);
		dispatch(fetchTickets(params, { transferred: category === 'transferred' }));
		if (category !== 'transferred') {
			dispatch(fetchUnseenTickets());
		}
	}

	const handleSelectionChange = (IDs, checked) => {
		const newSelection = checked
			? Array.from(new Set([...selection, ...IDs]))
			: selection.filter(id => !IDs.includes(id));
		setSelection(newSelection);
	};

	const isTransferred = category === 'transferred';
	const isClosed = category === 'closed'
		|| String(filters.status ?? '').split(',').map(status => status.trim().toLowerCase()).includes('closed');
	// Columns that only make sense inside their own category.
	const hiddenColumns = [
		...(isTransferred ? [] : ['transferred_to']),
		...(isClosed ? [] : ['closed_at', 'closed_by']),
	];
	const tableColumns = hiddenColumns.length === 0
		? columns
		: Object.fromEntries(Object.entries(columns).filter(([key]) => !hiddenColumns.includes(key)));

	return (
		<MainContainer>
			<Container fluid>
				<Row className='pt-4 pb-2'>
					<Col className='table-page-header'>
						<h5 className="flex-grow-1 m-0 text-align-left"><TicketCategoryInfo category={category} /></h5>
					</Col>
				</Row>
				<Row className="pb-3">
					<Col className="d-flex flex-wrap align-items-start gap-2">
						<TicketsSearch value={filters.text} onDebouncedChange={handleSearchDebounced} isLoading={isLoading} />
						{!isTransferred && (
							<TicketsQuickFilters
								data={data}
								filters={filters}
								onFilterChange={handleFilterChange}
								isLoading={isLoading}
								isCustomer={user?.isCustomer || user?.is_customer}
							/>
						)}
						<div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
							{!isTransferred && (
								<TicketsAction isLoading={isLoading} data={data} selection={selection} onActionStarted={actionStarted} onActionCompleted={actionCompleted} />
							)}
							<TicketsLayout columns={tableColumns} onColumnVisibilityChange={handleColumnVisibilityChange} isCustomer={user?.isCustomer || user?.is_customer} />
							{!isTransferred && (
								<>
									<TicketsFilter isLoading={isLoading} data={data} filters={filters} onFilterChange={handleFilterChange} isCustomer={user?.isCustomer || user?.is_customer} />
									<TicketsExport columns={tableColumns} isCustomer={user?.isCustomer || user?.is_customer} />
									{!user?.isCustomer && !user?.is_customer && (
										<Button
											variant="secondary"
											className="d-inline-flex align-items-center justify-content-center"
											style={{ overflow: 'visible' }}
											onClick={() => setShowSummaryModal(true)}
											disabled={isLoading}
											title="Email summary"
										>
											<BsEnvelope className="flex-shrink-0" size={18} aria-hidden />
										</Button>
									)}
									<LimitedNewButton isLoading={isLoading} showFormModal={() => setShowTicketModal(true)} single={'Ticket'} reducerKey='tickets' />
								</>
							)}
						</div>
					</Col>
				</Row>
				<Row>
					<Col className="overflow-x-auto">
						<TicketsTable
							tickets={tickets}
							total={total}
							columns={tableColumns}
							sort={sort}
							pagination={pagination}
							selection={selection}
							isLoading={isLoading}
							onSortChange={handleSortChange}
							onPaginationChange={handlePageChange}
							onSelectionChange={handleSelectionChange}
							unseenTickets={unseenTickets}
							isCustomer={user?.isCustomer || user?.is_customer}
							data={data}
							filters={filters}
							onTicketUpdate={reduxRefresh}
							readOnly={isTransferred}
						/>
					</Col>
				</Row>
			</Container>
			{
				showTicketModal && <TicketModal auxiliary={data} reduxRefresh={reduxRefresh} closeModal={() => setShowTicketModal(false)} />
			}
			{
				showSummaryModal && (() => {
					// Translate the existing /tickets filter shape into the modal's
					// (dateFrom, dateTo) pair so the modal opens pre-filled even
					// when the source filter is a preset like "this_week".
					// Preset translation aligned with SummaryEmailModal: "this week" and
					// "this month" mean from-start-to-today, not full calendar weeks/months.
					const pad = (n) => String(n).padStart(2, '0');
					const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
					const today = new Date();
					let presetFrom = null;
					let presetTo = null;
					switch (filters.created_at) {
						case 'today': {
							presetFrom = presetTo = iso(today);
							break;
						}
						case 'this_week': {
							// Monday of this week → today (or just today if today is Monday)
							const start = new Date(today);
							const day = start.getDay();
							start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
							presetFrom = iso(start);
							presetTo = iso(today);
							break;
						}
						case 'this_month': {
							const start = new Date(today.getFullYear(), today.getMonth(), 1);
							presetFrom = iso(start);
							presetTo = iso(today);
							break;
						}
						case 'range': {
							if (Array.isArray(filters.created_at_range)) {
								presetFrom = filters.created_at_range[0] ?? null;
								presetTo   = filters.created_at_range[1] ?? null;
							}
							break;
						}
						default:
							break;
					}
					return (
						<SummaryEmailModal
							closeModal={() => setShowSummaryModal(false)}
							initialDepartments={Array.isArray(filters.department) ? filters.department : []}
							initialStatuses={(() => {
								if (!filters.status || !Array.isArray(data.statuses)) return [];
								const names = String(filters.status).split(',').map(n => n.trim().toLowerCase()).filter(Boolean);
								return data.statuses
									.filter(s => names.includes(String(s.name).toLowerCase()))
									.map(s => s.id);
							})()}
							initialDateFrom={presetFrom}
							initialDateTo={presetTo}
						/>
					);
				})()
			}
		</MainContainer>
	)
}

export default Tickets
